import React, { useCallback, useEffect, useRef, useState } from "react";
import { axiosGet } from "../../apis/useAxios";
import { CrewActivityMobileViewComponent } from "./CrewActivityMobileViewComponent";
import {
  getCrewScheduleActivity,
  changeHotel,
  cancelHotel,
  changeResidenceLocation,
  cancelTransportation,
  session,
} from "../../apis/creweasyMobileApi";
import { stashGridState, popGridState } from "../../helpers/gridStateCache";

/**
 * Container for the crew-activity page: owns the CreweasyMobile dashboard call,
 * the header's staff dropdown and the write actions, and hands the payload to
 * CrewActivityMobileViewComponent.
 *
 * Endpoints
 *   POST /CreweasyMobile/get                       -> day groups + segments
 *   GET  /Crew/get-crew-dropdown-active                   -> Staff ID select box
 *   POST /CreweasyMobile/change-hotel              -> Change Hotel
 *   POST /CreweasyMobile/cancel-hotel              -> Cancel Hotel
 *   POST /CreweasyMobile/cancel-transportation     -> Cancel Ride / Cancel Shuttle
 *   POST /CreweasyMobile/change-residence-location -> Change residence address
 */
export const CrewActivityMobileViewContainer = ({
  employeeNumber: initialEmployee,
  onBack,
  history,
  location,
}) => {
  const s = session();
  const [airlineCode] = useState(s.airlineCode);

  // sourceUrl (small) travels through router state; sourceState (the calling grid's full
  // data snapshot) does not — history.pushState has a real size limit that a grid's loaded
  // rows can exceed, so it's handed off via an in-memory cache instead (see
  // helpers/gridStateCache.js). Both are captured once on mount so they survive the
  // staff-switch history.replace() below, which only ever carries { employeeNumber }.
  const [sourceUrl] = useState(() => location?.state?.sourceUrl ?? "/crew-activity");
  const [sourceState] = useState(() => popGridState());

  // Staff comes in through router state (history.push("/crew-activity-mobile", { employeeNumber }))
  // so the URL stays clean; falls back to the prop, then the session's own crew.
  // Router state is lost on refresh / direct URL, so mirror the pick in sessionStorage.
  const STORE_KEY = "crewActivityMobile_employeeNumber";
  const routeEmployee =
    location?.state?.employeeNumber ??
    (typeof window !== "undefined" ? window.sessionStorage.getItem(STORE_KEY) : null);
  const [employeeNumber, setEmployeeNumber] = useState(
    initialEmployee ?? routeEmployee ?? s.employeeNumber
  );

  // picked up when the grid navigates again while this view is already mounted
  useEffect(() => {
    if (routeEmployee && routeEmployee !== employeeNumber) setEmployeeNumber(routeEmployee);
  }, [routeEmployee]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (employeeNumber && typeof window !== "undefined") {
      window.sessionStorage.setItem(STORE_KEY, employeeNumber);
    }
  }, [employeeNumber]);

  // Back → whichever grid opened this view, restoring its saved state so it doesn't refetch.
  const goBack = () => {
    if (onBack) return onBack();
    if (history) {
      stashGridState(sourceState);
      return history.push(sourceUrl);
    }
    window.location.assign(sourceUrl);
  };
  const [days, setDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Guards the setState calls below against the "can't update state on an unmounted
  // component" warning — this view can be closed (CrewActivityMobileViewPopup unmounts it)
  // while getCrewScheduleActivity is still in flight.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const load = useCallback(
    async (empNo = employeeNumber) => {
      setIsLoading(true);
      try {
        const payload = await getCrewScheduleActivity({ airlineCode, employeeNumber: empNo });
        if (mountedRef.current) setDays(Array.isArray(payload) ? payload : []);
      } catch (e) {
        if (mountedRef.current) setDays([]);
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [airlineCode, employeeNumber]
  );

  useEffect(() => {
    load(employeeNumber);
  }, [load, employeeNumber]);

  /** GET /Crew/get-crew-dropdown-active — Staff ID select box */
  const fetchCrewDropdown = useCallback(async () => {
    const res = await axiosGet("/Crew/get-crew-dropdown-active", { AirlineCode: airlineCode });
    return res?.payload ?? [];
  }, [airlineCode]);

  const withReload = (fn) => async (...args) => {
    try {
      await fn(...args);
    } finally {
      await load();
    }
  };

  return (
    <CrewActivityMobileViewComponent
      dashboardData={days}
      airlineCode={airlineCode}
      employeeNumber={employeeNumber}
      isLoading={isLoading}
      onRefresh={() => load()}
      onBack={goBack}
      fetchCrewDropdown={fetchCrewDropdown}
      onSelectStaff={(crew) => {
        setEmployeeNumber(crew.employeeNumber);
        if (history) {
          history.replace("/crew-activity-mobile", {
            employeeNumber: crew.employeeNumber,
            sourceUrl,
          });
        }
      }}
      onChangeHotel={withReload((activity, hotelId) =>
        changeHotel({
          airlineCode,
          employeeNumber,
          transactionId: activity.hotelDetails?.transactionId ?? activity.transactionId,
          hotelId,
        })
      )}
      onCancelHotel={withReload((activity, options = {}) =>
        cancelHotel({
          airlineCode,
          employeeNumber,
          airportCode: activity.hotelDetails?.airportCode ?? activity.airportCode,
          transactionId: activity.hotelDetails?.transactionId ?? activity.transactionId,
          transporterRequired: options.transporterRequired === true,
          addressId: options.addressId ?? 0,
        })
      )}
      onCancelRide={withReload((activity, options = {}) =>
        cancelTransportation({
          airlineCode,
          employeeNumber,
          transactionId:
            activity.localTransportDetails?.transactionId ??
            activity.hotelTransportDetails?.transactionId ??
            activity.transactionId,
          reasonId: options.reasonId ?? 0,
          remark: options.remark ?? "",
        })
      )}
      onChangeHomeAddress={withReload((activity, addressId) =>
        changeResidenceLocation({
          airlineCode,
          employeeNumber,
          transactionId: activity.transactionId,
          addressId: Number(addressId) || 0,
        })
      )}
    />
  );
};

export default CrewActivityMobileViewContainer;
